import { motion } from "framer-motion";
import { Building2, CalendarRange, Users, Headset, MessageCircle, Phone } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const WHATSAPP_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER || "+966540423401";
const CALL_LINK = "tel:+966540423401";

const COPY = {
  en: {
    eyebrow: "Corporate & Events",
    title: "Corporate chauffeur bookings, hotel guest transfers, and event transport in Jeddah",
    intro:
      "BlackRide supports companies, executive assistants, concierge teams, event organisers, and premium travel planners who need dependable chauffeur service for VIP guests, airport arrivals, meetings, delegations, and coordinated movement in Jeddah.",
    points: [
      {
        icon: Building2,
        title: "Corporate travel management",
        desc: "Book executive transfers, airport arrivals, VIP client rides, and premium movement for business guests with a more polished service standard.",
      },
      {
        icon: CalendarRange,
        title: "Events and hospitality transport",
        desc: "Arrange premium chauffeur service for conferences, brand events, weddings, delegations, and private hospitality experiences in Jeddah.",
      },
      {
        icon: Users,
        title: "Multi-guest coordination",
        desc: "Useful for teams, speakers, investors, family groups, concierge referrals, and guests arriving on staggered schedules.",
      },
      {
        icon: Headset,
        title: "Direct planning support",
        desc: "Share your event or corporate travel requirement and BlackRide can help coordinate the most suitable premium vehicle and service plan.",
      },
    ],
    whatsapp: "Plan Corporate Booking",
    call: "Call BlackRide",
  },
  ar: {
    eyebrow: "الشركات والفعاليات",
    title: "حجوزات الشركات وتنقلات الفعاليات الفاخرة في جدة",
    intro:
      "تدعم بلاك رايد الشركات، ومساعدي التنفيذيين، وفرق الكونسيرج، ومنظمي الفعاليات، ومخططي السفر الراقي الذين يحتاجون إلى خدمة سائق خاص موثوقة للضيوف والاجتماعات والوصولات المهمة في جدة.",
    points: [
      {
        icon: Building2,
        title: "إدارة تنقلات الشركات",
        desc: "احجز تنقلات تنفيذية، واستقبالات المطار، وتنقلات كبار الشخصيات، والضيوف المهمين بمعيار خدمة أكثر احترافية.",
      },
      {
        icon: CalendarRange,
        title: "نقل الفعاليات والضيافة",
        desc: "نسق خدمة سائق فاخر للمؤتمرات، وفعاليات العلامات التجارية، والزفاف، والوفود، وتجارب الضيافة الخاصة في جدة.",
      },
      {
        icon: Users,
        title: "تنسيق مجموعات الضيوف",
        desc: "مناسب للفرق، والمتحدثين، والمستثمرين، والعائلات، وشركاء الكونسيرج، والضيوف القادمين في مواعيد مختلفة.",
      },
      {
        icon: Headset,
        title: "دعم مباشر للتخطيط",
        desc: "شارك تفاصيل متطلباتك للشركات أو الفعاليات، وسيساعدك بلاك رايد في تنسيق الخطة المناسبة وفئة المركبة الأنسب.",
      },
    ],
    whatsapp: "نسق حجز الشركات",
    call: "اتصل ببلاك رايد",
  },
};

export default function CorporateSection() {
  const { lang } = useLanguage();
  const copy = COPY[lang] || COPY.en;
  const whatsappMessage = encodeURIComponent(
    lang === "ar"
      ? "مرحباً بلاك رايد، أرغب في تنسيق حجز شركات / فعالية. يرجى التواصل معي بخصوص التفاصيل."
      : "Hello BlackRide, I would like to arrange a corporate / event chauffeur booking. Please contact me to plan the requirement."
  );
  const whatsappLink = `https://wa.me/${WHATSAPP_NUMBER.replace(/\+/g, "")}?text=${whatsappMessage}`;

  return (
    <section id="corporate" data-testid="corporate-section" className="relative py-20 md:py-24 bg-[#050505]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_0.95fr] gap-6 items-stretch">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="rounded-[32px] border border-white/8 bg-[#111] p-8 md:p-10"
          >
            <p className="text-xs uppercase tracking-[0.25em] text-[#C7A239] font-medium mb-4">{copy.eyebrow}</p>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl lg:text-5xl font-semibold text-white tracking-tight mb-5 max-w-3xl">
              {copy.title}
            </h2>
            <p className="text-base md:text-lg text-gray-400 leading-relaxed max-w-3xl mb-10">{copy.intro}</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {copy.points.map((item, index) => {
                const Icon = item.icon;

                return (
                  <motion.article
                    key={item.title}
                    initial={{ opacity: 0, y: 18 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.45, delay: index * 0.06 }}
                    className="rounded-[24px] border border-white/8 bg-[#0b0b0b] p-5"
                  >
                    <div className="w-11 h-11 rounded-2xl bg-[#C7A239]/10 flex items-center justify-center mb-4">
                      <Icon className="w-5 h-5 text-[#C7A239]" />
                    </div>
                    <h3 className="text-lg text-white font-medium mb-2">{item.title}</h3>
                    <p className="text-sm text-gray-400 leading-7">{item.desc}</p>
                  </motion.article>
                );
              })}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.65, delay: 0.1 }}
            className="rounded-[32px] border border-[#C7A239]/18 bg-[radial-gradient(circle_at_top,#241b08,transparent_45%),#111] p-8 md:p-10 flex flex-col justify-between"
          >
            <div>
              <div className="w-14 h-14 rounded-2xl bg-[#C7A239]/10 flex items-center justify-center mb-6">
                <Building2 className="w-7 h-7 text-[#C7A239]" />
              </div>
              <h3 className="font-['Playfair_Display'] text-3xl text-white mb-4">
                {lang === "ar" ? "دعنا ننسق النقل لعملائك أو ضيوفك" : "Let BlackRide coordinate transport for your guests"}
              </h3>
              <p className="text-gray-300 leading-8 mb-6">
                {lang === "ar"
                  ? "إذا كنت تخطط لاجتماعات تنفيذية، وصول وفود، فعاليات، أو حجوزات ضيافة راقية، يمكن لفريق بلاك رايد مساعدتك في تنظيم تجربة نقل أكثر سلاسة واحترافية."
                  : "If you are arranging executive meetings, VIP arrivals, delegations, hotel guest movement, or event transport, BlackRide can help you plan a smoother premium travel experience for every guest involved."}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row lg:flex-col gap-4">
              <a
                href={whatsappLink}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-[#C7A239] hover:bg-[#D4B34A] text-black font-medium px-6 py-4 rounded-full transition"
              >
                <MessageCircle className="w-5 h-5" />
                {copy.whatsapp}
              </a>
              <a
                href={CALL_LINK}
                className="inline-flex items-center justify-center gap-2 border border-white/15 hover:border-white/25 text-white px-6 py-4 rounded-full transition hover:bg-white/5"
              >
                <Phone className="w-5 h-5" />
                {copy.call}
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
